import { newTracker as newBrowserTracker, trackStructEvent } from '@snowplow/browser-tracker';
import { createIsbotFromList, list } from 'isbot';
import store from 'store2';
import uuid4 from 'uuid4';
import packageConfig from '../package.json';
import {
  GROUP_ID_TYPE_MAP,
  DEFAULT_OMITTED_TRACKING_PROVIDERS,
  SCROLL_PERCENTAGES,
  FIRST_SESSION_COOKIE_NAME,
  BOT_AGENTS,
  ONETRUST_COOKIE_NAME,
} from './constants';
import { browserCookieUtils, getTrackingCookies } from './cookies_utils';
import * as Helper from './helpers/helper';
import { PageHelper } from './helpers/helper';
import { browserMetadataUtils } from './helpers/metadata_utils';
import {
  globalProperties,
  eventProperties,
  clickSchemaProperties,
  scrollProperties,
} from './helpers/properties_helper';
import { SnowplowHelper } from './helpers/snowplow_helper';
import AdFraudDetection from './identifiers/ad_fraud_detection';
import Experimentation from './identifiers/experimentation';
import Identifier from './identifiers/identifiers';
import UserProfileService from './identifiers/user_profile_service';
import UserTargeting from './identifiers/user_targeting';
import { loadProviders } from './load_providers';
import PageView from './page_view';
import { dataLayerPush as tealiumDataLayerPush } from './providers/tealium_helper';
import Session from './session';
import trackError from './track_error';
import { Configuration } from './types/configuration';
import { CookieUtils } from './types/cookies_utils';
import { MetadataUtils } from './types/metadata_utils';
import { SnowplowUtils } from './types/snowplow_utils';
import Visitor from './visitor';

const ENABLE_ANALYTICS_LOGS = 'enable_analytics_logs';
const sessionStore = () => store.session;

const isbot = createIsbotFromList(list.concat(BOT_AGENTS));

class GustoAnalytics {
  _version: any;
  activeProviders: any[];
  adFraudDetection: AdFraudDetection;
  config: Configuration;
  cookieUtils: CookieUtils;
  currentPageView: any;
  experimentation: Experimentation;
  metadataUtils: MetadataUtils;
  pageHelper: PageHelper;
  session: Session;
  snowplowHelper: SnowplowHelper;
  userProfileService: UserProfileService;
  userTargeting: UserTargeting;
  visitor: Visitor;

  constructor(
    config: Configuration,
    cookieUtils: CookieUtils = browserCookieUtils,
    metadataUtils: MetadataUtils = browserMetadataUtils,
    snowplowUtils: SnowplowUtils = {
      newTracker: (...args: any[]) => {
        newBrowserTracker(...(args as Parameters<typeof newBrowserTracker>));
        // NOTE: It's important to explicitly return nothing here. The browser tracker does not
        // need to be used to fire events, so we don't want to return a tracker instance.
        return null;
      },
      trackStructEvent,
    },
  ) {
    this._version = packageConfig.version;
    this.activeProviders = [];
    this.config = config || {};
    this.cookieUtils = cookieUtils;

    const firstTouchRevenueForecastingEventEnabled =
      this.config.enableFirstTouchRevenueForecastingEvent !== false;
    const identifier = new Identifier(cookieUtils, firstTouchRevenueForecastingEventEnabled);
    this.adFraudDetection = new AdFraudDetection(identifier, cookieUtils);
    this.currentPageView = null;
    this.experimentation = new Experimentation(identifier, cookieUtils);
    this.pageHelper = new PageHelper(metadataUtils);
    this.metadataUtils = metadataUtils;
    this.session = new Session(cookieUtils);
    this.snowplowHelper = new SnowplowHelper(snowplowUtils, metadataUtils);
    this.userProfileService = new UserProfileService(identifier, cookieUtils);
    this.userTargeting = new UserTargeting(identifier, cookieUtils);
    this.visitor = new Visitor(identifier, cookieUtils);

    globalThis.GustoAnalytics = globalThis.GustoAnalytics || [];
  }

  get displayLog() {
    const defaultVal = this.config.environment === 'development';
    return sessionStore().get(ENABLE_ANALYTICS_LOGS, defaultVal);
  }

  set displayLog(val) {
    sessionStore()(ENABLE_ANALYTICS_LOGS, val);
  }

  log(...args: any[]) {
    if (this.displayLog === true) {
      globalThis.console.log('[ANALYTICS]', ...args);
    }
  }

  loadAnalytics() {
    this.log({
      fullstoryEnabled: this.config.fullstoryEnabled,
      snowplowEnabled: this.config.snowplowEnabled,
      autoPageEvent: this.autoPageEvent(),
      serverSideEligibleProviders: this.config.serverSideEligibleProviders,
      blockedEndpointsFromCookieSet: this.config.blockedEndpointsFromCookieSet,
    });

    if (this.config.snowplowEnabled) {
      this.snowplowHelper.init(
        this.config.snowplowTrackerName,
        this.config.snowplowTrackingUrl,
        this.config.snowplowAppId,
      );
    }

    this.activeProviders.push(
      ...loadProviders(
        {
          configs: this.config.providers,
          log: (...args: any[]) => this.log(...args),
        },
        this.snowplowHelper,
      ),
    );
  }

  analyticsReady(callback: any) {
    if (typeof callback !== 'function') {
      return;
    }

    // Set visitor ID if it doesn't exist
    this.experimentation.getExperimentationId();
    this.adFraudDetection.getAdFraudDetectionId();
    this.userProfileService.getUserProfileServiceId();
    this.userTargeting.getUserTargetingId();
    this.visitor.getVisitorId();

    // Set session_start_needed cookie is session ID doesn't exist
    // Set session ID if it doesn't exist
    this.session.setSessionStartNeeded();
    this.session.getSessionId();

    callback();
  }

  // Associate users and their actions
  // prop (hash parameters):
  //   userId = optional
  //   data = optional
  identify(prop: any = {}) {
    let data = Helper.deepCopyMerge({}, prop.data);
    // Try to get user ID from config if not passed in
    let userId = prop.userId || this.config.userId;
    let impersonatedUserId;
    const pandaAdminId = this.config.pandaAdminId;

    data.impersonated_user_id = null;
    if (pandaAdminId && userId && String(pandaAdminId) !== String(userId)) {
      impersonatedUserId = userId;
      data.impersonated_user_id = impersonatedUserId;
      userId = pandaAdminId;
    }

    const userRoleId = this.getUserRoleId();
    const sessionId = this.session.getSessionId();
    this.adFraudDetection.getAdFraudDetectionId();

    const oldVisitorId = this.visitor.getVisitorId();
    const oldVisitorIdOriginHash = this.visitor.getVisitorIdOriginHash();
    const newVisitorId = this.visitor.checkResetVisitorId(userId);
    const newVisitorIdOriginHash = this.visitor.getVisitorIdOriginHash();
    const visitorId = newVisitorId || oldVisitorId;
    this.userProfileService.checkResetUserProfileServiceId(userId);

    if (this.config.debugIdentify) {
      this.track({
        eventCategory: 'AnalyticsDebug',
        eventName: 'Identify',
        data: {
          identifyProperties: {
            userId,
            oldVisitorId,
            oldVisitorIdOriginHash,
            newVisitorId,
            newVisitorIdOriginHash,
            impersonatedUserId,
            visitorChanged: !!newVisitorId,
            source: 'js',
          },
          analyticsConfig: {
            userId: this.config.userId,
          },
        },
      });
    }

    this.log('identify args', arguments);

    // Add version number to data
    data.gusto_analytics_version = this._version;

    // Add panda admin ID to data if available
    if (pandaAdminId) {
      data.panda_admin_id = pandaAdminId;
    }

    // Add user role ID to data if available and not part of data already
    if (!data.user_role_id && userRoleId) {
      data.user_role_id = userRoleId;
    }

    // Add isBot to property to detect bot status through useragent
    data.is_bot = isbot(this.metadataUtils.getClientUserAgent());

    // Add tracking cookies to data, if data and tracking cookies have same key, the value in data take precedence
    data = Helper.deepCopyMerge(getTrackingCookies(this.cookieUtils), data);

    // Make a copy for in case data is modified
    // Add session ID to snowplow event data
    const dataCopy = Helper.deepCopyMerge({}, data, { session_id: sessionId });

    // If the identify call is intended for one of [AmplitudeProvider, FullstoryProvider]
    // We will pass the data directly to target provider and skip other providers for data security reason.
    if (prop.targetProvider) {
      const targetProvider = this.activeProviders.find(
        provider => provider.providerName() === prop.targetProvider,
      );
      if (targetProvider) {
        targetProvider.identify({ userId, visitorId, sessionId, data });
      }
      return;
    }

    if (this.config.snowplowEnabled) {
      const eventCategory = this.snowplowHelper.eventCategories().VISITOR_EVENT;
      const eventName = 'Identify';
      const schema = this.snowplowHelper.generateSchema('', 'identify', true);
      const serverSideEligibleProviders = this.config.serverSideEligibleProviders;
      const eventData = this.snowplowHelper.prepareData(
        userId,
        visitorId ?? '',
        dataCopy,
        serverSideEligibleProviders,
        this.userProfileService,
        this.userTargeting,
        this.experimentation,
        this.adFraudDetection,
      );

      this.log('identify snowplow', eventData);

      this.snowplowHelper.snowplowEvent(eventCategory, eventName, schema, eventData);
    }

    data = Helper.deepCopyMerge(this.getSinglePurposeIdentifiers(), dataCopy);
    this.activeProviders.forEach(provider => {
      if (!this.snowplowHelper.isBotUserAgent()) {
        provider.identify({ userId, visitorId, sessionId, data });
      }
    });
  }

  // Record page views
  // prop (hash parameters):
  //   userId = optional
  //   name = optional
  //   data = optional
  page(prop: any = {}) {
    let data = Helper.deepCopyMerge({}, prop.data);
    // Try to get user ID from config if not passed in
    let userId = prop.userId || this.config.userId;

    const name = prop.name;
    this.adFraudDetection.getAdFraudDetectionId();
    const visitorId = this.visitor.getVisitorId();
    const pandaAdminId = this.config.pandaAdminId;
    const userRoleId = this.getUserRoleId();
    const sessionId = this.session.getSessionId();

    this.currentPageView = new PageView();
    data.page_view_id = this.currentPageView.pageViewId();

    data.impersonated_user_id = null;
    if (pandaAdminId && userId && String(pandaAdminId) !== String(userId)) {
      data.impersonated_user_id = userId;
      userId = pandaAdminId;
    }

    this.log('page args', arguments);

    // Add version number to data
    data.gusto_analytics_version = this._version;

    // Add panda admin ID to data if available
    if (pandaAdminId) {
      data.panda_admin_id = pandaAdminId;
    }

    // Add user role ID to data if available and not part of data already
    if (!data.user_role_id && userRoleId) {
      data.user_role_id = userRoleId;
    }

    // Add isBot to property to detect bot status through useragent
    data.is_bot = isbot(this.metadataUtils.getClientUserAgent());

    // Add referrer, path, url, and title to data
    // Add tracking params (utm_*, vt_*, gclid) to data
    // Add tracking cookies to data
    // Values in developer specified data take precedence
    data = Helper.deepCopyMerge(
      this.pageHelper.getPageInfo(),
      this.pageHelper.getTrackingParams(),
      getTrackingCookies(this.cookieUtils),
      data,
    );

    // Make a copy in case data is modified
    // Add session ID to snowplow event data
    let dataCopy = Helper.deepCopyMerge({}, data, { session_id: sessionId });
    dataCopy = {
      ...dataCopy,
      url_query_params: this.pageHelper.getTrackingParams(),
      non_attribution_cookies: getTrackingCookies(this.cookieUtils),
    };

    if (this.config.snowplowEnabled) {
      const schema = this.snowplowHelper.generateSchema('', 'pageView', true);
      const serverSideEligibleProviders = this.config.serverSideEligibleProviders;
      const eventData = this.snowplowHelper.prepareData(
        userId,
        visitorId ?? '',
        dataCopy,
        serverSideEligibleProviders,
        this.userProfileService,
        this.userTargeting,
        this.experimentation,
        this.adFraudDetection,
      );

      this.log('page snowplow', name, schema, eventData);

      this.snowplowHelper.pageView(name, schema, eventData);
    }

    data = Helper.deepCopyMerge(this.getSinglePurposeIdentifiers(), dataCopy);

    this.activeProviders.forEach(provider => {
      if (!this.snowplowHelper.isBotUserAgent()) {
        provider.page({ pageName: name, userId, visitorId, sessionId, data });
      }
    });
  }

  // Associates identified user with a company/company lead
  // prop (hash parameters):
  //   groupId = 'L...' for company lead, 'C...' for company, 'A...' for accounting firm
  //   userId = optional
  //   data = optional
  group(prop: { groupId: string; userId?: string; data?: any }) {
    let groupType: string;
    let data = Helper.deepCopyMerge({}, prop.data);
    // Try to get user ID from config if not passed in
    let userId = prop.userId || this.config.userId;
    const groupId = prop.groupId;
    this.adFraudDetection.getAdFraudDetectionId();
    const visitorId = this.visitor.getVisitorId();
    const pandaAdminId = this.config.pandaAdminId;
    const userRoleId = this.getUserRoleId();
    const sessionId = this.session.getSessionId();

    data.impersonated_user_id = null;
    if (pandaAdminId && userId && String(pandaAdminId) !== String(userId)) {
      data.impersonated_user_id = userId;
      userId = pandaAdminId;
    }

    if (!groupId) {
      this.log('Must provide groupId');
      return;
    }

    this.log('group args', arguments);

    // Add version number to data
    data.gusto_analytics_version = this._version;

    // Add panda admin ID to data if available
    if (pandaAdminId) {
      data.panda_admin_id = pandaAdminId;
    }

    // Add user role ID to data if available and not part of data already
    if (!data.user_role_id && userRoleId) {
      data.user_role_id = userRoleId;
    }

    // Add isBot to property to detect bot status through useragent
    data.is_bot = isbot(this.metadataUtils.getClientUserAgent());

    // Add referrer, path, url, and title to data
    // Add tracking cookies to data
    // Values in developer specified data take precedence
    data = Helper.deepCopyMerge(
      this.pageHelper.getPageInfo(),
      getTrackingCookies(this.cookieUtils),
      data,
    );

    // Make a copy in case data is modified
    // Add session ID for snowplow event data
    const dataCopy = Helper.deepCopyMerge({}, data, { session_id: sessionId });

    if (this.config.snowplowEnabled) {
      const eventCategory = this.snowplowHelper.eventCategories().VISITOR_EVENT;
      const eventName = 'Group';
      const newGroupId = groupId.substring(1);

      groupType = GROUP_ID_TYPE_MAP[groupId[0] as keyof typeof GROUP_ID_TYPE_MAP];

      const schema = this.snowplowHelper.generateSchema('', 'group', true);

      dataCopy.group_id = newGroupId;
      dataCopy.type = groupType;
      const serverSideEligibleProviders = this.config.serverSideEligibleProviders;
      const eventData = this.snowplowHelper.prepareData(
        userId,
        visitorId ?? '',
        dataCopy,
        serverSideEligibleProviders,
        this.userProfileService,
        this.userTargeting,
        this.experimentation,
        this.adFraudDetection,
      );

      this.log('group snowplow', eventCategory, eventName, schema, eventData);

      this.snowplowHelper.snowplowEvent(eventCategory, eventName, schema, eventData);
    }

    data = Helper.deepCopyMerge(this.getSinglePurposeIdentifiers(), dataCopy);

    this.activeProviders.forEach(provider => {
      if (!this.snowplowHelper.isBotUserAgent()) {
        provider.group({ groupId, groupType, userId, visitorId, sessionId, data });
      }
    });
  }

  trackScrollEvent() {
    const scrollProps = scrollProperties();
    // Send only to snowplow for now
    if (SCROLL_PERCENTAGES.indexOf(scrollProps.percent) > -1) {
      this.track({
        targetProvider: 'snowplow',
        eventCategory: 'Page',
        eventName: 'Scroll',
        data: {
          ...scrollProps,
        },
      });
    }
  }

  trackDomEvent(event: any) {
    const { type } = event;
    const capitalizedEventType = type.charAt(0).toUpperCase() + type.slice(1);
    this.track({
      eventCategory: 'Dom',
      eventName: capitalizedEventType,
      data: {
        ...globalProperties(),
        ...clickSchemaProperties(event),
        event: eventProperties(event),
      },
    });
  }

  // prop (hash parameters):
  //   eventCategory
  //   eventName
  //   userId = optional
  //   data = optional
  //   callback = optional
  track(prop: any, isPii = false) {
    let data = Helper.deepCopyMerge({}, prop.data);
    // Try to get user ID from config if not passed in
    let userId = prop.userId || this.config.userId;
    const { eventCategory, eventName } = prop;

    if (!eventCategory || !eventName) {
      this.log('Must provide eventCategory and eventName');
      return;
    }

    this.adFraudDetection.getAdFraudDetectionId();
    const visitorId = this.visitor.getVisitorId();
    const pandaAdminId = this.config.pandaAdminId;
    const userRoleId = this.getUserRoleId();
    const sessionId = this.session.getSessionId();

    if (this.currentPageView) {
      data.page_view_id = this.currentPageView.pageViewId();
    }

    data.impersonated_user_id = null;
    if (pandaAdminId && userId && String(pandaAdminId) !== String(userId)) {
      data.impersonated_user_id = userId;
      userId = pandaAdminId;
    }

    this.log(`track${isPii ? 'Pii' : ''}`, arguments);

    // Add version number to data
    data.gusto_analytics_version = this._version;

    // Add panda admin ID to data if available
    if (pandaAdminId) {
      data.panda_admin_id = pandaAdminId;
    }

    // Add user role ID to data if available and not part of data already
    if (!data.user_role_id && userRoleId) {
      data.user_role_id = userRoleId;
    }

    // Add isBot to property to detect bot status through useragent
    data.is_bot = isbot(this.metadataUtils.getClientUserAgent());

    this.replaceDataUuidWithId(data, eventCategory, eventName);

    // Add referrer, path, url, and title to data
    // Add tracking cookies to data
    // Values in developer specified data take precedence
    data = Helper.deepCopyMerge(
      this.pageHelper.getPageInfo(),
      getTrackingCookies(this.cookieUtils),
      data,
    );

    // Make a copy in case data is modified
    // Add session ID for snowplow event data
    const dataCopy = Helper.deepCopyMerge({}, data, { session_id: sessionId });
    if (isPii) {
      dataCopy.isPii = true;
    }
    if (prop.targetProvider) {
      dataCopy.targetProvider = prop.targetProvider;
    }

    if (this.config.snowplowEnabled) {
      let isCompany = false;
      let newUserId;
      const schema = this.snowplowHelper.generateSchema(eventCategory, eventName, true);

      // Add type to properties if company event
      if (userId && userId[0] === 'C') {
        isCompany = true;
        dataCopy.type = 'company';
        newUserId = userId.substring(1);
      }

      const serverSideEligibleProviders = this.config.serverSideEligibleProviders;
      const eventData = this.snowplowHelper.prepareData(
        isCompany ? newUserId : userId,
        visitorId ?? '',
        dataCopy,
        serverSideEligibleProviders,
        this.userProfileService,
        this.userTargeting,
        this.experimentation,
        this.adFraudDetection,
      );

      this.log('track snowplow', eventCategory, eventName, schema, eventData);
      this.snowplowHelper.snowplowEvent(eventCategory, eventName, schema, eventData);
    }

    const providers = isPii
      ? this.activeProviders.filter(p => p.isApprovedProvider())
      : this.activeProviders;

    let targetProviders = providers.filter(
      p => !DEFAULT_OMITTED_TRACKING_PROVIDERS.includes(p.providerName()),
    );
    // If the identify call is intended for one of [AmplitudeProvider, FullstoryProvider]
    // We will pass the data directly to target provider and skip other providers.

    if (prop.targetProvider) {
      const targetProvider = providers.find(p => p.providerName() === prop.targetProvider);
      targetProviders = targetProvider ? [targetProvider] : [];
    }

    data = Helper.deepCopyMerge(this.getSinglePurposeIdentifiers(), dataCopy);

    targetProviders.forEach(provider => {
      if (!this.snowplowHelper.isBotUserAgent()) {
        provider.track({ eventCategory, eventName, userId, visitorId, sessionId, data });
      }
    });
  }

  // Same as track(), but only send to approved analytics tools
  //   eventCategory
  //   eventName
  //   userId = optional
  //   data = optional
  trackPii(prop: any) {
    this.track(prop, true);
  }

  // Track only snowplow, no other providers
  trackSnowplowOnly(prop: any) {
    const snowplowOnlyProps = { ...prop, targetProvider: 'snowplow' };
    this.track(snowplowOnlyProps, false);
  }

  triggerRevenueForecastingEvent(visitorId: string) {
    let firstSessionCookieSet = false;
    const cmsApplications = ['wp-talkshop', 'eng-blog', 'wordpress'];
    let parameters = { ...this.pageHelper.getPageInfo(), ...this.pageHelper.getTrackingParams() };
    if (!cmsApplications.includes(this.config.snowplowTrackerName)) {
      parameters.cookieReset = true;
    } else {
      firstSessionCookieSet = true;
    }
    parameters.visitor_id = visitorId;
    parameters = Helper.deepCopyMerge(getTrackingCookies(this.cookieUtils), parameters);
    parameters.url_query_params = this.pageHelper.getTrackingParams();
    parameters.non_attribution_cookies = getTrackingCookies(this.cookieUtils);
    this.track({
      eventCategory: 'RevenueForecasting',
      eventName: 'NewUserLanded',
      data: {
        ...parameters,
      },
    });
    if (firstSessionCookieSet) {
      this.storeFirstSessionIdCookie(this.session.getSessionId());
    }
  }

  // Wraps a provided function/callback with a call to track when invoked. props and isPii will be
  // passed directly to track, and the wrapped function's input/output interface will be preserved.
  wrapWithTracking(funcToWrap: any, prop: any, isPii = false) {
    return (...args: any[]) => {
      this.track(prop, isPii);
      return funcToWrap(...args);
    };
  }

  // TODO Delete this and update the pre-load script for a 1.0 release
  experimentEnrollment() {}

  // Push data to data layer
  dataLayer(data: any, isPii: boolean = false) {
    this.log('dataLayer', { data, isPii });

    // Tealium data layer can contain PII
    tealiumDataLayerPush(data);
  }

  dataLayerPii(data: any) {
    this.dataLayer(data, true);
  }

  // Provide gtmDataLayer alias while we transition to a service-agnostic name
  gtmDataLayer(data: any) {
    this.dataLayer(data);
  }

  autoPageEvent() {
    return !(this.config.autoPageEvent === false);
  }

  scrollEventsEnabled() {
    return this.config.scrollEventsEnabled === true;
  }

  visibilityEventsEnabled() {
    return this.config.visibilityEventsEnabled === true;
  }

  getPrivacyPreferences() {
    return this.snowplowHelper.addOnetrustCookies(this.cookieUtils.getCookie(ONETRUST_COOKIE_NAME));
  }

  // Return user role ID if found
  getUserRoleId() {
    return globalThis.Payroll?.Store?.user?.currentUserRole?.get('id');
  }

  getSinglePurposeIdentifiers() {
    const gexpId = this.experimentation.getExperimentationId();
    const upsId = this.userProfileService.getUserProfileServiceId();
    const userTargetId = this.userTargeting.getUserTargetingId();

    return {
      gexp_id: gexpId,
      ups_id: upsId,
      user_target_id: userTargetId,
    };
  }

  replaceDataUuidWithId(data: any, eventCategory: string, eventName: string) {
    // traverse the data object recursively and replace any employee uuid with the corresponding id
    if (typeof data !== 'object' || data === null) return;
    Object.keys(data).forEach(key => {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];
        if (typeof value === 'symbol') return;

        if (
          uuid4.valid(value) &&
          !this.pageHelper.EXCLUDED_UUID_EVENT_CATEGORIES.includes(eventCategory)
        ) {
          const analyticsId = this.getAnalyticsId(value);
          if (analyticsId !== value && !key.toLowerCase().includes('uuid')) {
            data[key] = analyticsId;

            data[`replace_uuid_${key}`] = value;
            trackError({
              error: {
                name: 'UuidTrackDataTranslated',
                message: `Uuid value (${value}) passed as data to GustoAnalytics#track, but was translated. Please use the numeric analyticsId for tracking`,
              },
              custom: {
                key,
                value,
                eventCategory,
                eventName,
              },
            });
          } else if (
            [
              'companyId',
              'company_id',
              'employeeId',
              'employee_id',
              'contractorId',
              'contractor_id',
              'payrollId',
              'payroll_id',
              'formId',
              'form_id',
              'personalDocumentId',
              'personal_document_id',
              'personalDocumentRequestId',
              'personal_document_request_id',
            ].includes(key)
          ) {
            trackError({
              error: {
                name: 'UuidTrackDataNotTranslated',
                message: `Uuid value (${value}) passed as data to GustoAnalytics#track, but was not translated to a numeric ID. Please use the numeric analyticsId for tracking`,
              },
              custom: {
                key,
                value,
                eventCategory,
                eventName,
              },
            });
          }
        } else {
          this.replaceDataUuidWithId(value, eventCategory, eventName);
        }
      }
    });
  }

  getAnalyticsId(value: string) {
    const companyId = this.getCompanyAnalyticsId(value);
    if (companyId !== value) {
      return companyId;
    }
    const employeeId = this.getEmployeeAnalyticsId(value);
    if (employeeId !== value) {
      return employeeId;
    }
    return value;
  }

  // Return company ID if found else return uuid
  getCompanyAnalyticsId(uuid: string) {
    const company = globalThis.Payroll?.Store?.company;

    if (!company || typeof company.get !== 'function') {
      return uuid;
    }

    return company.get('id') === uuid ? company.get('analytics_id') : uuid;
  }

  // Return employee ID if found else return uuid
  getEmployeeAnalyticsId(uuid: string) {
    const employees = globalThis.Payroll?.Store?.employees;

    if (!employees || typeof employees.get !== 'function') {
      return uuid;
    }

    const employee = employees.get(uuid);
    return employee ? employee.get('analytics_id') : uuid;
  }

  storeFirstSessionIdCookie(firstSessionId?: string) {
    this.cookieUtils.setCookie(FIRST_SESSION_COOKIE_NAME, firstSessionId, 45);
  }

  getPaidTrackingParams(url?: string) {
    return this.pageHelper.getTrackingParams(url);
  }
}

export default GustoAnalytics;
